import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, SlidersHorizontal } from "lucide-react";
import { GroupSortPanel } from "@/components/shared/GroupSortPanel";
import { storage } from "@/lib/storage";

// 378: Seskupit a řadit (jako v Dokladech, 377) pro ostatní seznamy — stejné tlačítko a bublina, jen pole podle dat stránky.
// groups = { k: { label, icon, key(row) → klíč skupiny, name?(klíč) → popisek, order?(a, b) → pevné pořadí, color?(klíč), dirs? } }
// orders = { k: { label, icon, dirs: [vzestupně, sestupně], def: "asc" | "desc", val(row) } }
// def = { g: "none" | k, gd: 0, k, dir }; uloženo v `storageKey` jako g|k|dir|gd (legacyKey = dřívější k|dir); tie(a, b) = pořadí při shodě;
// rank(row) = pevné pořadí před řazením (např. nezahájené zakázky v Deníku vždy dole).
const AZ = ["A → Z", "Z → A"];
const empty = (v) => v == null || v === "";
export const cmpVals = (a, b) => (typeof a === "number" || typeof b === "number" ? (a || 0) - (b || 0) : String(a).localeCompare(String(b), "cs", { numeric: true }));

// 379: uložené Zobrazení a tlačítko zvlášť — Úkoly a Docházka si skupiny skládají samy (Ke schválení / Hotové na konci,
// stránkování ze serveru), od useGroupSort berou jen tohle. orders zde stačí { label, icon, dirs, def }.
export function useStoredView(storageKey, def, { legacyKey, orders = {} } = {}) {
  const [view, setViewState] = useState(() => {
    const [g, k, d, gd] = String(storage.get(storageKey) || "").split("|");
    if (g && k) return { g, k, dir: d === "asc" ? "asc" : "desc", gd: gd === "1" ? 1 : 0 };   // pole se ověří až při vykreslení (může přibýt později, např. Cena díla)
    const [ok, od] = String((legacyKey && storage.get(legacyKey)) || "").split("|");
    return orders[ok] ? { ...def, k: ok, dir: od === "desc" || od === "-1" ? "desc" : od === "asc" || od === "1" ? "asc" : orders[ok].def || "asc" } : def;
  });
  const setView = useCallback((v) => { setViewState(v); storage.set(storageKey, `${v.g}|${v.k}|${v.dir}|${v.gd || 0}`); }, [storageKey]);
  return [view, setView];
}

// Platné pole (uložené mohlo zmizet) — g „none“ = bez skupin.
export const viewEff = (view, groups, orders, def) => ({ ...view, g: groups[view.g] ? view.g : "none", k: orders[view.k] ? view.k : def.k });

// Popis zobrazení pro řádek „Výchozí: …“ v bublině (380): „Stav · Datum vložení (nejnovější)“.
const low = (t) => (/→/.test(t) ? t : t.charAt(0).toLowerCase() + t.slice(1));
export function describeView(groups, orders, v) {
  const G = v.g !== "none" ? groups[v.g] : null, O = orders[v.k];
  return `${G ? G.label : "Bez skupin"} · ${O.label} (${low(O.dirs[v.dir === "asc" ? 0 : 1])})`;
}
export const isDefView = (v, def) => v.g === def.g && v.k === def.k && v.dir === def.dir && (v.gd || 0) === (def.gd || 0);

// Tlačítko Zobrazení (V) pro ListToolbar: bublina Seskupit a řadit. onFold(true / false) = Sbalit / Rozbalit vše (bez něj se nenabízí).
// 391: extra = další volba stránky v bublině (Objednávky: Částky) — { render(mobile), active, hint, def, reset }:
// active = liší se od výchozí (tlačítko svítí), def = doplněk řádku Výchozí, reset = Obnovit výchozí vrátí i ji.
export function groupSortProp({ groups, orders, view, setView, def, onFold, extra = null }) {
  const v = viewEff(view, groups, orders, def);
  const G = groups[v.g], O = orders[v.k];
  const groupFields = Object.entries(groups).map(([value, x]) => ({ value, label: x.label, icon: x.icon, dirs: x.dirs || AZ }));
  // u řazení je první vždy výchozí směr pole (A → Z nahoře ↑, Nejnovější ↓)
  const orderFields = Object.entries(orders).map(([value, x]) => ({ value, label: x.label, icon: x.icon,
    dirs: x.def === "desc" ? [x.dirs[1], x.dirs[0]] : x.dirs, icons: x.def === "desc" ? [ArrowDown, ArrowUp] : [ArrowUp, ArrowDown] }));
  const dirIdx = (k, dir) => ((orders[k].def === "desc") === (dir === "desc") ? 0 : 1);
  const idxDir = (k, i) => (i === 0 ? orders[k].def || "asc" : orders[k].def === "desc" ? "asc" : "desc");
  // 378: tlačítko jen „Zobrazení“ (V); co je nastavené, ukáže bublina po najetí.
  // 380: svítí, jen když se liší od výchozího — v bublině je vidět výchozí a Obnovit výchozí ho zhasne.
  const dflt = isDefView(v, def) && !extra?.active;
  return {
    icon: SlidersHorizontal, title: "Zobrazení", label: "Zobrazení", hotkey: "v", width: 620, active: !dflt,
    hint: `${G ? `${G.label} · ` : ""}${O.label} ${v.dir === "asc" ? "↑" : "↓"}${extra?.hint ? ` · ${extra.hint}` : ""}`,
    render: ({ mobile }) => (
      <GroupSortPanel mobile={mobile} groupFields={groupFields} group={G ? { k: v.g, dir: v.gd || 0 } : null}
        onGroup={(g) => setView({ ...v, g: g || "none", gd: 0 })} onGroupDir={(gd) => setView({ ...v, gd })}
        orderFields={orderFields} order={{ k: v.k, dir: dirIdx(v.k, v.dir) }}
        onOrder={(k) => setView({ ...v, k, dir: orders[k].def || "asc" })} onOrderDir={(i) => setView({ ...v, dir: idxDir(v.k, i) })}
        onFold={onFold} defText={`${describeView(groups, orders, def)}${extra?.def ? ` · ${extra.def}` : ""}`}
        onReset={dflt ? undefined : () => { setView({ ...def }); extra?.reset?.(); }} extra={extra?.render?.(mobile)} />
    ),
  };
}

export function useGroupSort(rows, { storageKey, legacyKey, groups, orders, def, tie, rank, extra }) {
  const [view, setView] = useStoredView(storageKey, def, { legacyKey, orders });
  const v = viewEff(view, groups, orders, def), kEff = v.k, gEff = v.g;
  const [closed, setClosed] = useState(() => new Set());
  useEffect(() => { setClosed(new Set()); }, [gEff]);
  const toggle = useCallback((key) => setClosed((cur) => { const n = new Set(cur); if (n.has(key)) n.delete(key); else n.add(key); return n; }), []);

  const sorted = useMemo(() => {
    const o = orders[kEff], sign = view.dir === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
      if (rank && rank(a) !== rank(b)) return rank(a) - rank(b);
      const va = o.val(a), vb = o.val(b);
      if (empty(va) !== empty(vb)) return empty(va) ? 1 : -1;   // bez hodnoty vždy na konec
      return (empty(va) ? 0 : cmpVals(va, vb)) * sign || (tie ? tie(a, b) : 0);
    });
  }, [rows, kEff, view.dir, orders, tie, rank]);
  const grouped = useMemo(() => {
    const G = groups[gEff];
    if (!G) return null;
    const map = new Map();
    sorted.forEach((r) => { const k = G.key(r) ?? ""; if (!map.has(k)) map.set(k, []); map.get(k).push(r); });
    const name = (k) => (G.name ? G.name(k) : k) || "";
    return [...map.entries()].sort(([a], [b]) => {
      if (!a !== !b) return a ? -1 : 1;   // „Bez …“ vždy na konec
      const c = G.order ? G.order(a, b) : name(a).localeCompare(name(b), "cs", { numeric: true });
      return view.gd ? -c : c;
    }).map(([key, list]) => ({ key, label: name(key) || G.none || "Bez hodnoty", rows: list, color: G.color?.(key) }));
  }, [sorted, groups, gEff, view.gd]);
  const shown = useMemo(() => (grouped ? grouped.filter((g) => !closed.has(g.key)).flatMap((g) => g.rows) : sorted), [grouped, closed, sorted]);

  const sortProp = groupSortProp({ groups, orders, view, setView, def, extra,
    onFold: (all) => setClosed(all ? new Set((grouped || []).map((g) => g.key)) : new Set()) });
  return { view: v, setView, sorted, groups: grouped, shown, closed, toggle, sortProp };
}

// Lišta skupiny v seznamu: šipka, barva / ikona, název, počet, vpravo volitelný doplněk; klik sbalí.
export function GroupHeader({ g, closed, onToggle, right, testid, mobile = false, card = false }) {
  return (
    <button type="button" onClick={() => onToggle(g.key)} aria-expanded={!closed} data-testid={testid || `group-${g.key || "none"}`}
      className={`w-full flex items-center gap-2.5 text-left transition-colors motion-reduce:transition-none ${card ? "rounded-[16px] border border-border bg-card px-4 min-h-[48px]" : "px-4 md:px-5 min-h-[44px] border-b border-border bg-muted/40"} hover:bg-muted/70`}>
      <ChevronDown size={16} className={`shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none ${closed ? "-rotate-90" : ""}`} />
      {g.color && <i className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: g.color }} />}
      <span className={`font-bold truncate min-w-0 ${mobile ? "text-[15px]" : "text-[14px]"}`}>{g.label}</span>
      <span className="text-[12.5px] font-semibold text-muted-foreground tabular-nums">{g.rows.length}</span>
      {right && <span className="ml-auto pl-3 text-[13px] text-muted-foreground tabular-nums whitespace-nowrap">{right}</span>}
    </button>
  );
}

// Karty (Investoři, Dodavatelé, Tým): se skupinami lišta skupiny jako karta a pod ní mřížka jejích karet.
export function GroupedGrid({ groups, rows, closed, onToggle, className, children }) {
  if (!groups) return <div className={className}>{rows.map(children)}</div>;
  return (
    <div className="space-y-3">
      {groups.map((g) => (
        <section key={g.key} className="space-y-3">
          <GroupHeader g={g} closed={closed?.has(g.key)} onToggle={onToggle} card />
          {!closed?.has(g.key) && <div className={className}>{g.rows.map(children)}</div>}
        </section>
      ))}
    </div>
  );
}
